import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Alert, App, Button, Card, Drawer, Spin, Switch, Tooltip, Typography } from 'antd';

import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import ModalActions from 'components/ModalActions';
import { API_MAP, agent, functions } from 'helpers';
import { constants } from 'helpers/constants';
import { EProductStatus, PRODUCT_STATUS_LABEL } from 'helpers/enums';
import DraftLabel from 'scenes/Producer/Product/components/DraftLabel';
import Alumy from 'scenes/Producer/Product/components/Partners/Alumy';
import Blinket from 'scenes/Producer/Product/components/Partners/Blinket';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { useAffiliatesProductStatusSummary } from 'v2/hooks/useAffiliatesProductStatusSummary';
import { useProductDeliveryMutation } from 'v2/hooks/useProductDeliveryMutation';
import { useProductInviteAffiliate } from 'v2/hooks/useProductInviteAffiliate';
import { useProductInvoices } from 'v2/hooks/useProductInvoices';
import { useProductStatusMutation } from 'v2/hooks/useProductStatusMutation';
import chatBotService, { Activity } from 'v2/services/chatBot';
import productService from 'v2/services/product';
import { ruleRunner, run } from 'validations/ruleRunner';
import { isEmail, required } from 'validations/rules';

import { Icon } from '@eduzz/ui-antd-theme';

import EditButton from './components/EditButton';
import QuickActions from './components/QuickActions';

const fieldValidations: Record<string, any> = {
  inputInviteAffiliate: ruleRunner('inputInviteAffiliate', 'e-mail', required, isEmail),
  inputDelivery: ruleRunner('inputDelivery', 'e-mail', required, isEmail)
};

interface IProps {
  active: boolean;
  changeActive: any;
  onEditProduct: any;
  productData: any;
  activatedChildren: any;
}

const getStatusAmountByStatus = (affiliatesProductStatusSummary: any, status: string): number => {
  let statusAmount =
    Array.isArray(affiliatesProductStatusSummary) &&
    affiliatesProductStatusSummary.find((x: any) => x.status == status)?.status_amount;

  if (!statusAmount) {
    statusAmount = 0;
  }

  return statusAmount;
};

const QuickView: React.FC<IProps> = ({ active, changeActive, onEditProduct, productData, activatedChildren }) => {
  const history = useHistory();
  const { message } = App.useApp();

  const statusMutation = useProductStatusMutation();
  const inviteAffiliateMutation = useProductInviteAffiliate();
  const deliveryMutation = useProductDeliveryMutation();
  const invoicesQuery = useProductInvoices(productData?.id);
  const affiliatesSummaryQuery = useAffiliatesProductStatusSummary(productData?.id);

  const [loading, setLoading] = useState(true);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [idProductHistory, setIdProductHistory] = useState<number>(null);
  const [modalStatusProduct, setModalStatusProduct] = useState(false);
  const [modalStatusProductArchive, setModalStatusProductArchive] = useState(false);
  const [modalCloneProduct, setModalCloneProduct] = useState(false);
  const [statusProduct, setStatusProduct] = useState<'active' | 'inactive' | 'filed' | 'draft' | ' '>(null);
  const [loadingStatusArchive, setLoadingStatusArchive] = useState(false);
  const [loadingCloneProduct, setLoadingCloneProduct] = useState(false);
  const [inputInviteAffiliate, setInputInviteAffiliate] = useState<string>(null);
  const [inputDelivery, setInputDelivery] = useState<string>(null);
  const [inputDeliveryError, setInputDeliveryError] = useState(false);
  const [actionButtonDisabled, setActionButtonDisabled] = useState(false);
  const [numberAffiliates, setNumberAffiliates] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasDelivery, setHasDelivery] = useState(false);

  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  const statusProductRef = useRef(statusProduct);
  statusProductRef.current = statusProduct;

  const inputInviteAffiliateRef = useRef(inputInviteAffiliate);
  inputInviteAffiliateRef.current = inputInviteAffiliate;

  const validationErrorsRef = useRef(validationErrors);
  validationErrorsRef.current = validationErrors;

  const prevActiveRef = useRef(active);

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const isValid = useCallback(
    (field: any) => {
      return !!validationErrors[field];
    },
    [validationErrors]
  );

  const onValidation = useCallback((field: string) => {
    const currentErrors = { ...validationErrorsRef.current };
    const obj = run({ inputInviteAffiliate: inputInviteAffiliateRef.current }, [fieldValidations[field]]);

    currentErrors[field] = obj[field];

    for (const prop in currentErrors) {
      if (!currentErrors[prop]) {
        delete currentErrors[prop];
      }
    }

    setValidationErrors(currentErrors);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    if (typeof changeActive === 'function') {
      changeActive();
    }

    setLoading(true);
    setIdProductHistory(productDataRef.current?.id);
  }, [changeActive]);

  const toggleModalStatusProduct = useCallback(() => {
    const product = productDataRef.current;

    if (Blinket.isBlinket(product.cnt_partner_cod)) {
      Blinket.blinketModal();
      return;
    }

    setModalStatusProduct(prev => !prev);
  }, []);

  const onCancelModal = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  const onConfirmModal = useCallback(async () => {
    toggleModalStatusProduct();
    setActionButtonDisabled(true);

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: statusProductRef.current === 'active' ? EProductStatus.INACTIVE : EProductStatus.ACTIVE
      });

      setStatusProduct(statusProductRef.current === 'active' ? 'inactive' : 'active');
      setActionButtonDisabled(false);

      message.success('Produto alterado com sucesso');
      onEditProduct();
    } catch {
      setActionButtonDisabled(false);
      message.error('Ops, erro ao atualizar o status do produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProduct, message]);

  const toggleModalStatusProductArchive = useCallback(() => {
    const product = productDataRef.current;

    if (Alumy.isAlumy(product.cnt_partner_cod)) {
      Alumy.alumyModal();
      return;
    }

    if (Blinket.isBlinket(product.cnt_partner_cod)) {
      Blinket.blinketModal();
      return;
    }

    setModalStatusProductArchive(prev => !prev);
  }, []);

  const onCancelModalArchive = useCallback(() => {
    toggleModalStatusProductArchive();
  }, [toggleModalStatusProductArchive]);

  const onConfirmModalArchive = useCallback(async () => {
    setLoadingStatusArchive(true);
    setActionButtonDisabled(true);
    toggleModalStatusProductArchive();

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: EProductStatus.ARCHIVE
      });

      setStatusProduct('filed');
      setLoadingStatusArchive(false);
      setActionButtonDisabled(false);

      onEditProduct();
      handleCloseQuickView();
    } catch {
      setLoadingStatusArchive(false);
      setActionButtonDisabled(false);
      message.error('Ops, não foi possível excluir o produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProductArchive, handleCloseQuickView, message]);

  const toggleModalCloneProduct = useCallback(() => {
    const product = productDataRef.current;

    if (Blinket.isBlinket(product.cnt_partner_cod)) {
      Blinket.blinketModal();
      return;
    }

    setModalCloneProduct(prev => !prev);
  }, []);

  const onCancelModalCloneProduct = useCallback(() => {
    toggleModalCloneProduct();
  }, [toggleModalCloneProduct]);

  const onConfirmModalCloneProduct = useCallback(async () => {
    toggleModalCloneProduct();
    setLoadingCloneProduct(true);
    setActionButtonDisabled(true);
    setCloneLoading(true);

    try {
      const result = await productService.cloneProduct(productDataRef.current.id);
      const clonedProduct = result?.product ?? result;
      history.push('/producer/product/editproduct/' + clonedProduct.id);
      functions.addScrollPage();
    } catch {
      message.error('Ops, erro ao clonar o produto');
      setLoadingCloneProduct(false);
      setCloneLoading(false);
      setActionButtonDisabled(false);
    }
  }, [history, toggleModalCloneProduct, message]);

  const handleChange = useCallback(
    (prop: any, event: any) => {
      if (prop === 'inputInviteAffiliate') {
        setInputInviteAffiliate(event.target.value);
        onValidation(prop);
      }

      if (prop === 'inputDelivery') {
        setInputDeliveryError(false);
        setInputDelivery(event.target.value);
        setValidationErrors(prev => {
          if (!prev.inputDelivery) {
            return prev;
          }

          const next = { ...prev };
          delete next.inputDelivery;
          return next;
        });
      }
    },
    [onValidation]
  );

  const handleClickInviteAffiliate = useCallback(async () => {
    const errors = run({ inputInviteAffiliate: inputInviteAffiliateRef.current }, [
      fieldValidations.inputInviteAffiliate
    ]);

    setValidationErrors(prev => {
      const next = { ...prev };

      if (errors.inputInviteAffiliate) {
        next.inputInviteAffiliate = errors.inputInviteAffiliate;
      } else {
        delete next.inputInviteAffiliate;
      }

      return next;
    });

    if (errors.inputInviteAffiliate) {
      return;
    }

    try {
      await inviteAffiliateMutation.mutateAsync({
        productId: productDataRef.current.id,
        email: inputInviteAffiliateRef.current
      });

      setInputInviteAffiliate('');
      setNumberAffiliates(prev => prev + 1);

      onEditProduct();
      message.success('Sucesso, afiliado adicionado com êxito');
    } catch (error) {
      message.error(error);
    }
  }, [inviteAffiliateMutation, onEditProduct, message]);

  const handleClickDelivery = useCallback(async () => {
    const product = productDataRef.current;
    const errors = run({ inputDelivery }, [fieldValidations.inputDelivery]);

    setValidationErrors(prev => {
      const next = { ...prev };

      if (errors.inputDelivery) {
        next.inputDelivery = errors.inputDelivery;
      } else {
        delete next.inputDelivery;
      }

      return next;
    });

    if (errors.inputDelivery) {
      return;
    }

    setInputDeliveryError(false);

    try {
      if (Alumy.isAlumy(product.cnt_partner_cod)) {
        await Alumy.alumyProductDelivery(product.id, inputDelivery);
      } else {
        await deliveryMutation.mutateAsync({
          productId: product.id,
          customerEmail: inputDelivery
        });
      }

      setInputDelivery('');
      setInputDeliveryError(false);
      message.success('Sucesso, arquivo entregue com êxito');
    } catch (error: any) {
      if (typeof error === 'string') {
        message.error(error);
        return;
      }

      message.error('Ops, erro na entrega manual');
    }
  }, [deliveryMutation, inputDelivery, message]);

  const handleClickGetInvoice = useCallback(async () => {
    if (!productDataRef.current) {
      return;
    }

    try {
      await invoicesQuery.refetch();
    } catch {
      message.error('Ops, erro nos dados da fatura');
    }
  }, [invoicesQuery, message]);

  const handleClickGetAffiliatesProductStatusSummary = useCallback(async () => {
    if (!productDataRef.current) {
      return;
    }

    try {
      await affiliatesSummaryQuery.refetch();
    } catch {
      message.error('Ops, erro nos dados dos afiliados');
    }
  }, [affiliatesSummaryQuery, message]);

  const handleChangeStatusProduct = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  const copiedCheckout = useCallback(() => {
    chatBotService.activity(Activity.copyCheckoutLink);
  }, []);

  useEffect(() => {
    const wasInactive = prevActiveRef.current === false;
    prevActiveRef.current = active;

    if (!wasInactive || !active) {
      return;
    }

    if (productData.id && productData.id !== idProductHistory) {
      setLoading(true);
      setInputDeliveryError(false);

      (async () => {
        const { data } = await agent.get(`${API_MAP.productHasDelivery}`.replace('{id}', productData.id));
        setLoading(false);
        setInputDelivery(idProductHistory === productData.id ? inputDelivery : '');
        setInputDeliveryError(false);
        setInputInviteAffiliate(idProductHistory === productData.id ? inputInviteAffiliate : '');
        setStatusProduct(productData.status);
        setNumberAffiliates(productData.affiliates);
        setHasDelivery(!!data);
      })();
    } else {
      setLoading(false);
      setActionButtonDisabled(false);
    }
  }, [active, productData, idProductHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!productData) {
    return (
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={false}
        closable={false}
        width='min(450px, 100vw)'
      />
    );
  }

  const productDataReceive = productData;
  const physicalProductPending = productDataReceive.type === 3 && productDataReceive.moderation_status === 'P';
  const affiliatesSummaryData = affiliatesSummaryQuery.data;
  const approvedAffiliatesAmount = getStatusAmountByStatus(affiliatesSummaryData, 'A');
  const disapprovedAffiliatesAmount = getStatusAmountByStatus(affiliatesSummaryData, 'R');
  const pendingAffiliatesAmount = getStatusAmountByStatus(affiliatesSummaryData, 'P');
  const cantManualDelivery = productDataReceive.has_children || statusProduct === 'inactive' || !hasDelivery;

  const labelToggleStatus = statusMutation.isPending ? <Spin size='small' /> : PRODUCT_STATUS_LABEL[statusProduct];

  const dontShowArchiveVariationTootip = productDataReceive.parent_product == 0 || activatedChildren > 2;

  return (
    <React.Fragment>
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={active}
        width='min(450px, 100vw)'
        title={`Produto: ${productDataReceive.id}`}
        footer={
          statusProduct !== 'filed' ? (
            <PermissionHide action='myeduzz:meus-produtos:editar'>
              <Tooltip
                title={dontShowArchiveVariationTootip ? '' : 'Ao excluir essa variação todo o Produto será excluído'}
              >
                <Button
                  id='quickview-product-delete-button'
                  danger
                  variant='filled'
                  block
                  color='danger'
                  onClick={toggleModalStatusProductArchive}
                  disabled={actionButtonDisabled}
                  icon={loadingStatusArchive && statusMutation.isPending ? <Spin size='small' /> : <Icon.Trash2 />}
                >
                  Excluir
                </Button>
              </Tooltip>
            </PermissionHide>
          ) : null
        }
      >
        {loading ? (
          <div className='loading flex h-full items-center justify-center'>
            <Spin size='large' />
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            {statusProduct !== 'filed' && (
              <div className='flex gap-2'>
                {(productDataReceive.has_children
                  ? productDataReceive.type === constants.INFO ||
                    productDataReceive.type === constants.PHYSICAL ||
                    productDataReceive.type === constants.TICKET
                  : productDataReceive.type === constants.INFO ||
                    productDataReceive.type === constants.PHYSICAL ||
                    productDataReceive.type === constants.TICKET ||
                    productDataReceive.type === constants.SERVICE) && (
                  <div className='min-w-0 flex-1'>
                    <EditButton
                      id={productDataReceive.id}
                      parentId={productDataReceive.parent_product}
                      onClick={functions.addScrollPage}
                      disabled={actionButtonDisabled}
                    />
                  </div>
                )}

                {!Alumy.isAlumy(productDataReceive.cnt_partner_cod) &&
                  !productDataReceive.has_children &&
                  productDataReceive.type !== 2 && (
                    <PermissionHide action='myeduzz:meus-produtos:editar'>
                      <div className='min-w-0 flex-1'>
                        <Button
                          id='quickview-product-copy-button'
                          block
                          color='default'
                          variant='filled'
                          onClick={toggleModalCloneProduct}
                          disabled={actionButtonDisabled}
                          icon={loadingCloneProduct && cloneLoading ? <Spin size='small' /> : <Icon.Copy size={16} />}
                        >
                          Copiar
                        </Button>
                      </div>
                    </PermissionHide>
                  )}
              </div>
            )}

            {!!productDataReceive.todo && (
              <Alert
                type='warning'
                message={
                  <>
                    <Typography.Text strong>Atualize seu modelo de comissão para porcentagem</Typography.Text>
                    <Typography.Paragraph>
                      Você ainda tem comissões por valor cadastradas em seus produtos!
                    </Typography.Paragraph>
                    <Typography.Paragraph>Atualize suas comissões para o modelo de porcentagem.</Typography.Paragraph>
                  </>
                }
              />
            )}

            <div className='flex items-center gap-4'>
              {productDataReceive.image ? (
                <img src={productDataReceive.image} alt='' className='size-14 shrink-0 rounded-lg object-cover' />
              ) : (
                <div className='size-14 shrink-0 rounded-lg bg-(--uizz-antd-color-fill-tertiary)' />
              )}

              <div className='flex min-w-0 flex-1 flex-col gap-1'>
                <Typography.Text strong ellipsis title={productDataReceive.title || 'Produto desconhecido'}>
                  {productDataReceive.title || 'Produto desconhecido'}
                </Typography.Text>

                {statusProduct !== 'draft' ? (
                  <div className='flex items-center gap-2'>
                    <Switch
                      size='small'
                      defaultChecked={statusProduct === 'active'}
                      checked={statusProduct === 'active'}
                      disabled={statusProduct === 'filed' || statusMutation.isPending}
                      onChange={handleChangeStatusProduct}
                    />
                    <Typography.Text type='secondary'>{labelToggleStatus}</Typography.Text>
                  </div>
                ) : (
                  <DraftLabel />
                )}
              </div>
            </div>

            {!productDataReceive.has_children && (
              <Card size='small'>
                <div className='flex'>
                  <div className='flex flex-1 flex-col items-center gap-0.5'>
                    <Typography.Text type='secondary'>Ganhos</Typography.Text>
                    <Typography.Text strong type='success'>
                      <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                        <ColoredFormattedNumber
                          value={productDataReceive.total_gains}
                          format={productDataReceive.currency?.code}
                        />
                      </PermissionHide>
                      <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                        oculto
                      </PermissionHide>
                    </Typography.Text>
                    {(productDataReceive.charge_type === 'A' ||
                      productDataReceive.charge_type === 'N' ||
                      !productDataReceive.charge_type ||
                      productDataReceive.charge_type === ' ' ||
                      productDataReceive.charge_type === '') && (
                      <>
                        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                          <div className='mt-3 flex flex-col items-center'>
                            <Typography.Text strong>
                              {productDataReceive.charge_type === 'A' && (
                                <CalculateCommissionAffiliate
                                  price={productDataReceive.recurring_value}
                                  commissionType={productDataReceive.recurring_comission_type}
                                  commissionValue={productDataReceive.recurring_comission_value}
                                />
                              )}
                              {productDataReceive.charge_type === 'N' && (
                                <CalculateCommissionAffiliate
                                  price={productDataReceive.price || 0}
                                  commissionType={productDataReceive.comission_type}
                                  commissionValue={productDataReceive.comission_value}
                                />
                              )}
                              {(!productDataReceive.charge_type ||
                                productDataReceive.charge_type === ' ' ||
                                productDataReceive.charge_type === '') && (
                                <CalculateCommissionAffiliate price={0} commissionValue={0} />
                              )}
                            </Typography.Text>
                            <Typography.Text type='secondary'>de Comissão</Typography.Text>
                          </div>
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                          <Typography.Text type='secondary' className='mt-3'>
                            oculto
                          </Typography.Text>
                        </PermissionHide>
                      </>
                    )}
                  </div>

                  <div className='flex flex-1 flex-col items-center gap-0.5'>
                    <Typography.Text type='secondary'>Afiliados</Typography.Text>
                    <Typography.Text strong>{numberAffiliates}</Typography.Text>
                    <div className='mt-3 flex flex-col items-center'>
                      <Typography.Text strong>{productDataReceive.rpc_affiliate || 0}</Typography.Text>
                      <Typography.Text type='secondary'>RPC/A</Typography.Text>
                    </div>
                  </div>

                  <div className='flex flex-1 flex-col items-center gap-0.5'>
                    <Typography.Text type='secondary'>Funil Infinito</Typography.Text>
                    <Typography.Text strong type={productDataReceive.infinity_funnel ? 'success' : undefined}>
                      {productDataReceive.infinity_funnel ? 'Ativado' : 'Desativado'}
                    </Typography.Text>
                    <div className='mt-3 flex flex-col items-center'>
                      <Typography.Text strong>{productDataReceive.rpc_producer || 0}</Typography.Text>
                      <Typography.Text type='secondary'>RPC/P</Typography.Text>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <QuickActions
              productDataReceive={productDataReceive}
              statusProduct={statusProduct}
              physicalProductPending={physicalProductPending}
              copiedCheckout={copiedCheckout}
              handleChange={handleChange}
              inputInviteAffiliate={inputInviteAffiliate}
              productData={productData}
              isValid={isValid}
              handleClickInviteAffiliate={handleClickInviteAffiliate}
              isInvitingAffiliate={inviteAffiliateMutation.isPending}
              errorFor={errorFor}
              handleClickGetAffiliatesProductStatusSummary={handleClickGetAffiliatesProductStatusSummary}
              affiliatesStatusSummary={affiliatesSummaryData}
              isLoadingAffiliatesSummary={affiliatesSummaryQuery.isFetching}
              approvedAffiliatesAmount={approvedAffiliatesAmount}
              disapprovedAffiliatesAmount={disapprovedAffiliatesAmount}
              pendingAffiliatesAmount={pendingAffiliatesAmount}
              cantManualDelivery={cantManualDelivery}
              inputDeliveryError={inputDeliveryError}
              inputDelivery={inputDelivery}
              handleClickDelivery={handleClickDelivery}
              isDelivering={deliveryMutation.isPending}
              handleClickGetInvoice={handleClickGetInvoice}
              productInvoice={invoicesQuery.data}
              isLoadingInvoice={invoicesQuery.isFetching}
            />
          </div>
        )}
      </Drawer>

      <ModalActions
        open={modalStatusProduct}
        onClose={onCancelModal}
        onOk={onConfirmModal}
        title={`Deseja realmente ${statusProduct === 'active' ? 'inativar' : 'ativar'} este produto? `}
        buttonOk={statusProduct === 'active' ? 'Inativar' : 'Ativar'}
      >
        {statusProduct === 'active' && productDataReceive.has_children
          ? 'Atenção, enquanto o produto pai estiver inativo todas suas variações também ficarão inativas no checkout.'
          : ''}
      </ModalActions>

      <ModalActions
        open={modalStatusProductArchive}
        onClose={onCancelModalArchive}
        onOk={onConfirmModalArchive}
        title='Deseja realmente excluir este produto?'
        buttonOk={'Excluir'}
        buttonOkColor='var(--uizz-antd-color-error)'
      >
        <Typography.Paragraph>
          {`Atenção, esta operação é irreversível${productData.has_children ? ' e automaticamente todas variações serão excluídas.' : '.'}`}{' '}
        </Typography.Paragraph>
      </ModalActions>

      <ModalActions
        open={modalCloneProduct}
        onClose={onCancelModalCloneProduct}
        onOk={onConfirmModalCloneProduct}
        title={'Deseja realmente duplicar este produto?'}
        buttonOk={'Duplicar'}
      >
        <Typography.Paragraph>Obs: você será redirecionado para a tela de edição.</Typography.Paragraph>
      </ModalActions>
    </React.Fragment>
  );
};

export default QuickView;
